import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import { Wrapper } from '@googlemaps/react-wrapper';
import { toInteger } from 'lodash';
import getConfig from '@config';
import { routes } from '@routes';
import { useSearch } from '@contexts/search-context';
import { formatWholeDollarAmount } from '@utils/money';
import { concatenateAndReplace } from '@utils/test-helpers';
import palette from '@color-palette';
import { Map, MapMarker, MapMarkerDefaultIcon } from '@components/map';
import { ListingMapCard, ListingPartial } from './listing-map-card';
import { ListingsArray } from '../types';
import { CARD_HEIGHT, CARD_OFFSET_Y, CARD_WIDTH, CARD_OFFSET_X } from './constants';
const MapOverlay = dynamic(() => import('./map-overlay'), {
  ssr: false
});
function computeOverlayPosition(map: google.maps.Map | undefined, listing: ListingPartial): {
  lat: number;
  lng: number;
} {
  if (!listing.lat || !listing.lng) {
    return {
      lat: listing.lat ?? 0,
      lng: listing.lng ?? 0
    };
  }
  const bounds = map?.getBounds();
  const projection = map?.getProjection();
  const lat = (() => {
    if (!bounds || !projection || !map) return listing.lat;
    const mapHeight = map.getDiv().offsetHeight;
    const scale = Math.pow(2, map.getZoom() || 0);
    const markerLatLng = new google.maps.LatLng(listing.lat, listing.lng);
    const markerPoint = projection.fromLatLngToPoint(markerLatLng);
    const nePoint = projection.fromLatLngToPoint(bounds.getNorthEast());
    if (!markerPoint || !nePoint) return listing.lat;
    let y = (markerPoint.y - nePoint.y) * scale;
    const cardTop = y - CARD_OFFSET_Y;
    const cardBottom = cardTop + CARD_HEIGHT;
    if (cardTop < 0) y += -cardTop;else if (cardBottom > mapHeight) y -= cardBottom - mapHeight;
    const adjustedPoint = new google.maps.Point(markerPoint.x, nePoint.y + y / scale);
    return projection.fromPointToLatLng(adjustedPoint)?.lat() || listing.lat;
  })();
  const lng = (() => {
    if (!bounds || !map) return listing.lng;
    const proj = map.getProjection();
    if (!proj) return listing.lng;
    const mapWidth = map.getDiv().offsetWidth;
    const markerPoint = proj.fromLatLngToPoint(new google.maps.LatLng(listing.lat, listing.lng));
    const swPoint = proj.fromLatLngToPoint(bounds.getSouthWest());
    if (!markerPoint || !swPoint) return listing.lng;
    const scale = Math.pow(2, map.getZoom() || 0);
    const x = (markerPoint.x - swPoint.x) * scale;
    const cardRightEdge = x + CARD_WIDTH - CARD_OFFSET_X;
    const cardLeftEdge = x - CARD_OFFSET_X;
    if (cardRightEdge > mapWidth) {
      const adjustedX = (mapWidth - CARD_WIDTH + CARD_OFFSET_X - 10) / scale;
      return proj.fromPointToLatLng(new google.maps.Point(swPoint.x + adjustedX, markerPoint.y))?.lng() || listing.lng;
    }
    if (cardLeftEdge < 0) {
      const adjustedX = (CARD_OFFSET_X + 10) / scale;
      return proj.fromPointToLatLng(new google.maps.Point(swPoint.x + adjustedX, markerPoint.y))?.lng() || listing.lng;
    }
    return listing.lng;
  })();
  return {
    lat,
    lng
  };
}
interface SearchMapViewProps {
  isListActive: boolean;
  map: google.maps.Map | undefined;
  mapListings: ListingsArray;
  hoveredListingSlug: string | undefined;
  selectedListing: ListingPartial | undefined;
  onMapClick: (event: google.maps.MapMouseEvent) => void;
  onDragEnd: (map: google.maps.Map) => void;
  onZoomChanged: (map: google.maps.Map) => void;
  onResize: (map: google.maps.Map) => void;
  onMapCardClose: () => void;
  onSelectListing: (id: string) => void;
  onUpdateFavorite: (listing: ListingPartial, isFavorited: boolean) => void;
}
export const SearchMapView: React.FC<SearchMapViewProps> = t0 => {
  const $ = _c(29);
  const {
    isListActive,
    map,
    mapListings,
    hoveredListingSlug,
    selectedListing,
    onMapClick,
    onDragEnd,
    onZoomChanged,
    onResize,
    onMapCardClose,
    onSelectListing,
    onUpdateFavorite
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getConfig();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    publicRuntimeConfig: t2
  } = t1;
  const {
    google: t3
  } = t2;
  const {
    apiKey
  } = t3;
  const {
    centerPoint,
    geoSearchBox,
    mapZoom,
    adultCount,
    childCount,
    infantCount,
    petCount,
    checkInDate,
    checkOutDate
  } = useSearch();
  let t4;
  if ($[1] !== isListActive) {
    t4 = cx("right-0 bottom-0 w-full max-h-[calc(100vh-80px)] googlemap:w-auto lg:pt-0", {
      "top-[78px] lg:top-[80px] h-[calc(100vh-78px)] lg:h-[calc(100vh-80px)] lg:w-full min-[1600px]:w-full googlemap:w-full googlemap:left-0 fixed lg:block": !isListActive
    }, {
      "hidden absolute top-[80px] lg:h-[calc(100vh-80px)] lg:block lg:w-1/3 min-[1536px]:w-[calc(100vw-1024px)] googlemap:left-1024px": isListActive
    });
    $[1] = isListActive;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== adultCount || $[4] !== centerPoint || $[5] !== checkInDate || $[6] !== checkOutDate || $[7] !== childCount || $[8] !== geoSearchBox || $[9] !== hoveredListingSlug || $[10] !== infantCount || $[11] !== map || $[12] !== mapListings || $[13] !== mapZoom || $[14] !== onDragEnd || $[15] !== onMapCardClose || $[16] !== onMapClick || $[17] !== onResize || $[18] !== onSelectListing || $[19] !== onUpdateFavorite || $[20] !== onZoomChanged || $[21] !== petCount || $[22] !== selectedListing) {
    t5 = centerPoint && (geoSearchBox || mapZoom) && <Map style={{
      height: "100%",
      width: "100%",
      outline: "none"
    }} center={centerPoint} zoom={mapZoom} gestureHandling="greedy" clickableIcons={false} disableDefaultUI={true} zoomControl={true} zoomControlOptions={{
      position: 9
    }} onDragEnd={onDragEnd} onZoomChanged={onZoomChanged} onResize={onResize} onClick={onMapClick} minZoom={2} maxZoom={17}>{mapListings.map((listing, index) => {
        if (!listing.lat || !listing.lng) {
          return null;
        }
        return <MapMarker key={listing.slug} hover={listing.slug === hoveredListingSlug} zIndex={listing.slug === hoveredListingSlug ? mapListings.length + 1 : mapListings.length - index} icon={{
          ...MapMarkerDefaultIcon,
          fillColor: listing.slug === hoveredListingSlug || listing.slug === selectedListing?.slug ? palette.gray.main : palette.primary.main,
          scale: listing.slug === hoveredListingSlug || listing.slug === selectedListing?.slug ? 14 : 9
        }} data-testid={concatenateAndReplace(`${listing.name}-map`)} position={{
          lat: listing.lat,
          lng: listing.lng
        }} onClick={() => onSelectListing(listing.id)} priceLabel={listing.priceDisplay?.baseAmountCents && listing.priceDisplay?.nights ? formatWholeDollarAmount(toInteger((listing.priceDisplay.subtotalWithoutTaxesCents || listing.priceDisplay.baseAmountCents) / listing.priceDisplay.nights)) : undefined} index={index} />;
      })}{selectedListing && selectedListing.slug && selectedListing.lat && selectedListing.lng && <MapOverlay position={computeOverlayPosition(map, selectedListing)}><div className="w-[325px] hidden lg:block"><ListingMapCard listing={selectedListing} isFavorited={selectedListing.isFavorited} closeMapCard={onMapCardClose} onFavoritedSuccess={isNowFavorited => {
            onUpdateFavorite(selectedListing, isNowFavorited);
          }} listingRoute={routes.listings({
            slug: selectedListing.slug,
            adultCount,
            childCount,
            infantCount,
            petCount,
            checkInDate: checkInDate || undefined,
            checkOutDate: checkOutDate || undefined
          })} /></div></MapOverlay>}</Map>;
    $[3] = adultCount;
    $[4] = centerPoint;
    $[5] = checkInDate;
    $[6] = checkOutDate;
    $[7] = childCount;
    $[8] = geoSearchBox;
    $[9] = hoveredListingSlug;
    $[10] = infantCount;
    $[11] = map;
    $[12] = mapListings;
    $[13] = mapZoom;
    $[14] = onDragEnd;
    $[15] = onMapCardClose;
    $[16] = onMapClick;
    $[17] = onResize;
    $[18] = onSelectListing;
    $[19] = onUpdateFavorite;
    $[20] = onZoomChanged;
    $[21] = petCount;
    $[22] = selectedListing;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t5) {
    t6 = <div className="absolute inset-0" data-testid="@map/map-area" role="application" aria-label="Search map"><Wrapper apiKey={apiKey}>{t5}</Wrapper></div>;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t4 || $[27] !== t6) {
    t7 = <div className={t4}>{t6}</div>;
    $[26] = t4;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
};